// tslint:disable: variable-name

import { coerceNumberProperty, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  HostBinding,
} from '@angular/core';
import buildPaginationArray from './pagination-utils';
import { Observable, Subject } from 'rxjs';

/** The default page size if there is no page size and there are no provided page size options. */
const DEFAULT_PAGE_SIZE = 50;

/**
 * Change event object that is emitted when the user selects a
 * different page size or navigates to another page.
 */
export class PageEvent {
  /** The current page index. */
  pageIndex: number;

  /**
   * Index of the page that was selected previously.
   */
  previousPageIndex: number;

  /** The current page size */
  pageSize: number;

  /** The current total number of items being paged */
  length: number;
}


/**
 * Component to provide navigation between paged inforsukaion. Displays the size of the current
 * page, user-selectable options to change that size, what items are being shown, and
 * navigational button to go to the previous or next page.
 */
@Component({
  selector: 'suka-pagination',
  exportAs: 'sukaPagination',
  template: `
    <div class="suka-pagination-outer-container">
      <div class="suka-pagination-container">
        <div class="suka-pagination-results">
          Mostrando <span class="suka-pagination-result">{{getRangeLabel(pageIndex, pageSize, length)}}</span>
        </div>

        <div class="suka-pagination-actions">
          <button
            type="button" class="suka-pagination-navigation-first"
            (click)="firstPage()"
            [disabled]="_previousButtonsDisabled()"
            *ngIf="showFirstLastButtons"
            sukaButton="plain"
            size="sm"
          >
            <suka-icon icon="chevrons-left" class="suka-pagination-icon"></suka-icon>
          </button>
          <button
            type="button" class="suka-pagination-navigation-previous"
            (click)="previousPage()"
            [disabled]="_previousButtonsDisabled()"
            sukaButton="plain"
            size="sm"
          >
            <suka-icon icon="chevron-left" class="suka-pagination-icon"></suka-icon>
            Anterior
          </button>
          <button
            *ngFor="let pageNumber of _pagesArray"
            type="button"
            class="suka-pagination-navigation-page"
            (click)="goToPage(pageNumber)"
            [disabled]="pageNumber === '...'"
            sukaButton="plain"
            size="sm"
            [ngClass]="{
              'suka-pagination-navigation-page--selected': pageNumber === pageIndex + 1
            }"
          >
            {{pageNumber}}
          </button>
          <button
            type="button"
            class="suka-pagination-navigation-next"
            (click)="nextPage()"
            [disabled]="_nextButtonsDisabled()"
            sukaButton="plain"
            size="sm"
          >
            Siguiente
            <suka-icon icon="chevron-right" class="suka-pagination-icon"></suka-icon>
          </button>
          <button
            type="button" class="suka-pagination-navigation-last"
            (click)="lastPage()"
            [disabled]="_nextButtonsDisabled()"
            *ngIf="showFirstLastButtons"
            sukaButton="plain"
            size="sm"
          >
            <suka-icon icon="chevrons-right" class="suka-pagination-icon"></suka-icon>
          </button>
        </div>
      </div>
    </div>

  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Pagination implements OnInit {
  private _initialized: boolean;
  initialized = new Subject();

  @Input() disabled = false;
  @HostBinding('class.suka-pagination') baseClass = true;

  /** The zero-based page index of the displayed list of items. Defaulted to 0. */
  @Input()
  get pageIndex(): number { return this._pageIndex; }
  set pageIndex(value: number) {
    this._pageIndex = Math.max(coerceNumberProperty(value), 0);
    this._changeDetectorRef.markForCheck();
  }

  private _pageIndex = 0;

  /** The length of the total number of items that are being paginated. Defaulted to 0. */
  @Input()
  get length(): number { return this._length; }
  set length(value: number) {
    this._length = coerceNumberProperty(value);
    this._changeDetectorRef.markForCheck();
    this._updateDisplayedPagesArray();
  }
  private _length = 0;

  /** Number of items to display on a page. By default set to 20. */
  @Input()
  get pageSize(): number { return this._pageSize; }
  set pageSize(value: number) {
    this._pageSize = Math.max(coerceNumberProperty(value), 0);
    this._updateDisplayedPageSizeOptions();
    this._updateDisplayedPagesArray();
  }
  private _pageSize: number;

  /** The set of provided page size options to display to the user. */
  @Input()
  get pageSizeOptions(): number[] { return this._pageSizeOptions; }
  set pageSizeOptions(value: number[]) {
    this._pageSizeOptions = (value || []).map(p => coerceNumberProperty(p));
    this._updateDisplayedPageSizeOptions();
  }
  private _pageSizeOptions: number[] = [];

  /** Whether to hide the page size selection UI from the user. */
  @Input()
  get hidePageSize(): boolean { return this._hidePageSize; }
  set hidePageSize(value: boolean) {
    this._hidePageSize = coerceBooleanProperty(value);
  }
  private _hidePageSize = false;


  /** Whether to show the first/last buttons UI to the user. */
  @Input()
  get showFirstLastButtons(): boolean { return this._showFirstLastButtons; }
  set showFirstLastButtons(value: boolean) {
    this._showFirstLastButtons = coerceBooleanProperty(value);
  }
  private _showFirstLastButtons = false;

  /** Event emitted when the pagination changes the page size or page index. */
  @Output() readonly page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

  /** Displayed set of page size options. Will be sorted and include current page size. */
  _displayedPageSizeOptions: number[];

  /** Displayed pages array */
  _pagesArray = [];

  constructor(private _changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit() {
    this._initialized = true;
    this.initialized.next(true);
    this._updateDisplayedPageSizeOptions();
  }

  /** Advances to the next page if it exists. */
  nextPage(): void {
    if (!this.hasNextPage()) { return; }

    const previousPageIndex = this.pageIndex;
    this.pageIndex++;
    this._updateDisplayedPagesArray();
    this._emitPageEvent(previousPageIndex);
  }

  /** Move back to the previous page if it exists. */
  previousPage(): void {
    if (!this.hasPreviousPage()) { return; }

    const previousPageIndex = this.pageIndex;
    this.pageIndex--;
    this._updateDisplayedPagesArray();
    this._emitPageEvent(previousPageIndex);
  }

  /** Move to the first page if not already there. */
  firstPage(): void {
    // hasPreviousPage being false implies at the start
    if (!this.hasPreviousPage()) { return; }

    const previousPageIndex = this.pageIndex;
    this.pageIndex = 0;
    this._updateDisplayedPagesArray();
    this._emitPageEvent(previousPageIndex);
  }

  /** Move to the last page if not already there. */
  lastPage(): void {
    // hasNextPage being false implies at the end
    if (!this.hasNextPage()) { return; }

    const previousPageIndex = this.pageIndex;
    this.pageIndex = this.getNumberOfPages() - 1;
    this._updateDisplayedPagesArray();
    this._emitPageEvent(previousPageIndex);
  }

  goToPage(page: number): void {
    const previousPageIndex = this.pageIndex;

    this.pageIndex = page - 1;
    this._updateDisplayedPagesArray();
    this._emitPageEvent(previousPageIndex);
  }

  /** Whether there is a previous page. */
  hasPreviousPage(): boolean {
    return this.pageIndex >= 1 && this.pageSize !== 0;
  }

  /** Whether there is a next page. */
  hasNextPage(): boolean {
    const maxPageIndex = this.getNumberOfPages() - 1;
    return this.pageIndex < maxPageIndex && this.pageSize !== 0;
  }

  /** Calculate the number of pages */
  getNumberOfPages(): number {
    if (!this.pageSize) {
      return 0;
    }

    return Math.ceil(this.length / this.pageSize);
  }

  /** Calculate the page range */
  getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) { return `0 de ${length}`; }

    length = Math.max(length, 0);

    const startIndex = page * pageSize;

    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex = startIndex < length ?
      Math.min(startIndex + pageSize, length) :
      startIndex + pageSize;

    return `${startIndex + 1} â ${endIndex} de ${length}`;
  }


  /**
   * Changes the page size so that the first item displayed on the page will still be
   * displayed using the new page size.
   *
   * For example, if the page size is 10 and on the second page (items indexed 10-19) then
   * switching so that the page size is 5 will set the third page as the current page so
   * that the 10th item will still be displayed.
   */
  _changePageSize(pageSize: number) {
    // Current page needs to be updated to reflect the new page size. Navigate to the page
    // containing the previous page's first item.
    const startIndex = this.pageIndex * this.pageSize;
    const previousPageIndex = this.pageIndex;

    this.pageIndex = Math.floor(startIndex / pageSize) || 0;
    this.pageSize = pageSize;
    this._emitPageEvent(previousPageIndex);
  }

  /** Checks whether the buttons for going forwards should be disabled. */
  _nextButtonsDisabled() {
    return this.disabled || !this.hasNextPage();
  }

  /** Checks whether the buttons for going backwards should be disabled. */
  _previousButtonsDisabled() {
    return this.disabled || !this.hasPreviousPage();
  }

  /**
   * Updates the list of page size options to display to the user. Includes making sure that
   * the page size is an option and that the list is sorted.
   */
  private _updateDisplayedPageSizeOptions() {
    if (!this._initialized) { return; }

    // If no page size is provided, use the first page size option or the default page size.
    if (!this.pageSize) {
      this._pageSize = this.pageSizeOptions.length !== 0 ?
        this.pageSizeOptions[0] :
        DEFAULT_PAGE_SIZE;
    }

    this._displayedPageSizeOptions = this.pageSizeOptions.slice();

    if (this._displayedPageSizeOptions.indexOf(this.pageSize) === -1) {
      this._displayedPageSizeOptions.push(this.pageSize);
    }

    // Sort the numbers using a number-specific sort function.
    this._displayedPageSizeOptions.sort((a, b) => a - b);
    this._changeDetectorRef.markForCheck();
  }

  private _updateDisplayedPagesArray() {
    this._pagesArray = buildPaginationArray(this.getNumberOfPages(), this.pageIndex + 1, 3);
  }

  /** Emits an event notifying that a change of the pagination's properties has been triggered. */
  private _emitPageEvent(previousPageIndex: number) {
    this.page.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }
}
